import React, { useState } from 'react';
import { ArrowRightIcon, CheckIcon, ChevronDownIcon, FileTextIcon } from 'lucide-react';
import { images } from '../../data/images';
import { flagshipDifferentiators, flagshipIncludes, flagshipSpecs } from '../../data/solutions';
import { Button } from '../ui/Button';
import { DotMark } from '../ui/DotMark';
import { Reveal } from '../ui/Reveal';
import { SupplyTag } from '../ui/SupplyTag';
export function Flagship() {
  const [specsOpen, setSpecsOpen] = useState(false);
  return <section id="flagship" aria-labelledby="flagship-title" className="relative overflow-hidden bg-afb-night text-white" data-id="element-255" data-mp="12qyxgf5bk7hy">
      <div aria-hidden="true" className="dot-field absolute inset-0" data-id="element-256" data-mp="17j8ojyi67dlt" />
      <div className="relative mx-auto max-w-page px-5 py-20 lg:px-8 lg:py-28" data-id="element-257" data-mp="1chtm1q61fj6k">
        <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-end" data-id="element-258" data-mp="1hgdb3zfxhb35">
          <Reveal data-id="element-259" data-mp="1hi7sx4vwkn30">
            <p className="flex items-center gap-2.5 text-sm text-white/70" data-id="element-260" data-mp="1lhyi545s6v5f">
              <DotMark data-id="element-261" data-mp="1li8ct51rtqhg" />
              Flagship system
            </p>
            <h2 id="flagship-title" className="mt-5 font-sora text-[2.25rem] font-semibold leading-[1.05] tracking-[-0.025em] md:text-[3.25rem]" data-id="element-262" data-mp="1ic02j6rqrvay">
              All-in-One Power System
            </h2>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/75" data-id="element-263" data-mp="18elv1wnsqgus">
              One container with everything a site needs to generate, store and manage its own power. We deliver it,
              integrate it and commission it. After that, it runs plug-and-play.
            </p>
          </Reveal>

          <Reveal delay={60} data-id="element-264" data-mp="1rbn1zn0wur1d">
            <div className="rounded-card border border-white/10 bg-afb-night-2 p-6 md:p-8" data-id="element-265" data-mp="1rav94jyp77es">
              <p className="text-sm text-white/60" data-id="element-266" data-mp="1mdbtpgq9kol9">Deployment time</p>
              <dl className="mt-5 grid grid-cols-2 gap-6" data-id="element-267" data-mp="2jrxqbj5hpr5c">
                <div data-id="element-268" data-mp="2jtikv8rt1a37">
                  <dt className="sr-only" data-id="element-269" data-mp="2ju0oiqgsqql5">Initial setup</dt>
                  <dd className="font-sora text-5xl font-semibold tracking-[-0.03em] md:text-6xl" data-id="element-270" data-mp="2f068bx0egtqt">
                    3–4<span className="ml-1 text-2xl text-white/70" data-id="element-271" data-mp="d505k59w5f42">hrs</span>
                  </dd>
                  <dd className="mt-2 text-[15px] text-white/70" data-id="element-272" data-mp="i1gtayd6hrgm">Initial setup on site</dd>
                </div>
                <div data-id="element-273" data-mp="3dcishoa4m8x">
                  <dt className="sr-only" data-id="element-274" data-mp="3brmamiemt8f">Setup thereafter</dt>
                  <dd className="font-sora text-5xl font-semibold tracking-[-0.03em] text-afb-turquoise md:text-6xl" data-id="element-275" data-mp="8bnteumef0s7">
                    ~30<span className="ml-1 text-2xl text-afb-turquoise/80" data-id="element-276" data-mp="3qhaa7ebf94y7">min</span>
                  </dd>
                  <dd className="mt-2 text-[15px] text-white/70" data-id="element-277" data-mp="3ui2ia48k53sw">Each setup after that</dd>
                </div>
              </dl>
              <div className="mt-6 flex items-center gap-3" aria-hidden="true" data-id="element-278" data-mp="3lj8pkoyckxkm">
                <span className="h-2.5 w-2.5 rounded-full bg-white" data-id="element-279" data-mp="3llboxzle4320" />
                <span className="energy-trace flex-1" data-id="element-280" data-mp="1etkdjwxgyta1" />
                <span className="h-2.5 w-2.5 rounded-full bg-afb-turquoise" data-id="element-281" data-mp="1jsnizihd31ij" />
                <span className="energy-trace flex-1" data-id="element-282" data-mp="34a66k099etty" />
                <span className="h-2.5 w-2.5 rounded-full bg-afb-lime" data-id="element-283" data-mp="34aobdl9zmgdh" />
              </div>
              <div className="mt-2 flex justify-between text-xs text-white/55" aria-hidden="true" data-id="element-284" data-mp="2zcee954pd5p8">
                <span data-id="element-285" data-mp="3e0rck8r9j0mw">Delivered</span>
                <span data-id="element-286" data-mp="3e1sr4n7hm59d">Set up</span>
                <span data-id="element-287" data-mp="398px6dysstsn">Running</span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-12" data-id="element-288" data-mp="3mzuwfey97k2g">
          <div className="relative overflow-hidden rounded-card" data-id="element-289" data-mp="3myta2ebsfb5f">
            <img src={images.flagship} alt="Illustrative render of an All-in-One containerised power system with its doors open, showing cabinets and storage tanks" className="aspect-[16/9] w-full object-cover" data-id="element-290" data-mp="3i2cp1szvxcwk" />
            <span className="absolute right-3 top-3 rounded-md bg-black/60 px-2 py-1 text-[11px] text-white/85" data-id="element-291" data-mp="22kt15y8m42ii">
              Illustrative render
            </span>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:gap-16" data-id="element-292" data-mp="oormjja9u0vy">
          <Reveal data-id="element-293" data-mp="oqvse6rqd9yv">
            <h3 className="font-sora text-xl font-semibold" data-id="element-294" data-mp="tnm3sgslbhdy">Why it’s different</h3>
            <dl className="mt-6 grid gap-x-10 gap-y-6 sm:grid-cols-2" data-id="element-295" data-mp="tpp5hy33uyyi">
              {flagshipDifferentiators.map(item => <div key={item.title} className="border-t border-white/10 pt-5" data-id="element-296" data-mp="12p2dsnfhthhn">
                  <dt className="font-semibold text-white" data-id="element-297" data-mp="5ya01wg181j1">{item.title}</dt>
                  <dd className="mt-1.5 text-[15px] leading-relaxed text-white/70" data-id="element-298" data-mp="5w6yqg9filz0">{item.detail}</dd>
                </div>)}
            </dl>
          </Reveal>

          <Reveal delay={60} data-id="element-299" data-mp="ey6ihgbtw45n">
            <h3 className="font-sora text-xl font-semibold" data-id="element-300" data-mp="ex53j0royxin">What you get</h3>
            <ul className="mt-6 space-y-3" data-id="element-301" data-mp="jr9erw7zprpj">
              {flagshipIncludes.map(item => <li key={item} className="flex items-start gap-3 text-[15px] text-white/85" data-id="element-302" data-mp="3jcoe22y8diku">
                  <CheckIcon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-afb-turquoise" data-id="element-303" data-mp="3ndgmwv36xlgl" />
                  {item}
                </li>)}
            </ul>
          </Reveal>
        </div>

        <div className="mt-12 border-t border-white/10 pt-6" data-id="element-304" data-mp="3awyu6xxsvcop">
          <button type="button" onClick={() => setSpecsOpen(open => !open)} aria-expanded={specsOpen} aria-controls="flagship-specs" className="flex w-full items-center justify-between rounded-btn py-2 text-left font-sora text-lg font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-afb-turquoise" data-id="element-305" data-mp="36tsphuqc5tkc">
            Specifications
            <ChevronDownIcon aria-hidden="true" className={`h-5 w-5 text-white/70 transition-transform duration-200 ${specsOpen ? 'rotate-180' : ''}`} data-id="element-306" data-mp="2n74hcgdhdo4g" />
          </button>
          <div id="flagship-specs" className="collapse-grid" data-open={specsOpen} data-id="element-307" data-mp="2e555e2hbpa8d">
            <div className="overflow-hidden" data-id="element-308" data-mp="297bw30kq403v">
              <table className="mt-4 w-full text-left text-[15px]" data-id="element-309" data-mp="1zjhgk406dhj6">
                <caption className="sr-only" data-id="element-310" data-mp="1zlaodmmiy7sr">All-in-One Power System specifications</caption>
                <tbody className="divide-y divide-white/10" data-id="element-311" data-mp="24jlzs1sgwqd3">
                  {flagshipSpecs.map(spec => <tr key={spec.label} data-id="element-312" data-mp="1pnef70d5dmxa">
                      <th scope="row" className="py-3.5 pr-6 font-normal text-white/70" data-id="element-313" data-mp="1ujblseylzewv">
                        {spec.label}
                      </th>
                      <td className="py-3.5 text-right text-white" data-id="element-314" data-mp="2ibsc1csi2d79">
                        {spec.value ?? <SupplyTag tone="dark" label="From spec sheet" data-id="element-315" data-mp="2n80mwdznzk8m" />}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-3 sm:flex-row" data-id="element-316" data-mp="318jrfb4x8861">
          <Button href="#contact" data-id="element-317" data-mp="3178rxniidmkq">
            Request a demo
            <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-318" data-mp="35zjtsvwt7ifx" />
          </Button>
          <Button href="#resources" variant="outline-dark" data-id="element-319" data-mp="ujrima2r7nwg">
            <FileTextIcon aria-hidden="true" className="h-4 w-4" data-id="element-320" data-mp="ui870ws1m110" />
            Get the brochures
          </Button>
        </div>
      </div>
    </section>;
}